import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/glucose";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Waveform, Pulse, Notebook, Gear, SignOut, Users, ChartLine, Sparkle, Sun, Moon } from "@phosphor-icons/react";
import { useTheme } from "../lib/theme";

const NAV = [
  { to: "/dashboard", label: "Live", icon: Pulse, testId: "nav-live" },
  { to: "/reports", label: "Reports", icon: ChartLine, testId: "nav-reports" },
  { to: "/treatments", label: "Treatments", icon: Notebook, testId: "nav-treatments" },
  { to: "/watchers", label: "Followers", icon: Users, testId: "nav-watchers" },
  { to: "/settings", label: "Settings", icon: Gear, testId: "nav-settings" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [isPro, setIsPro] = useState(false);
  const { theme, toggle: toggleTheme } = useTheme();

  useEffect(() => {
    api.get("/subscription/status").then((r) => setIsPro(!!r.data.is_pro && !r.data.expired)).catch(() => {});
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 bg-background border-b border-border/60">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-3 flex items-center gap-4 sm:gap-8">
          <Link to="/dashboard" className="flex items-center gap-2 min-w-0" data-testid="brand-link">
            <div className="w-8 h-8 rounded-md bg-foreground text-background grid place-items-center flex-none">
              <Waveform size={18} weight="duotone" />
            </div>
            <span className="font-display font-semibold text-lg tracking-tight hidden xs:inline sm:inline">GlucoCloud</span>
            {isPro && <span className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 bg-foreground text-background rounded" data-testid="pro-badge">Pro</span>}
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((n) => {
              const active = location.pathname.startsWith(n.to);
              const Icon = n.icon;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={n.testId}
                  className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-2 transition-colors ${
                    active
                      ? "bg-secondary text-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                  }`}
                >
                  <Icon size={16} weight={active ? "fill" : "regular"} />
                  {n.label}
                </Link>
              );
            })}
          </nav>

          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            <button
              onClick={toggleTheme}
              className="w-8 h-8 grid place-items-center rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors flex-none"
              data-testid="theme-toggle"
              aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              title={theme === "dark" ? "Light mode" : "Dark mode"}
            >
              {theme === "dark" ? <Sun size={16} weight="fill" /> : <Moon size={16} weight="duotone" />}
            </button>
            {!isPro && (
              <Link to="/pricing" data-testid="upgrade-link">
                <Button size="sm" variant="outline" className="h-8 gap-1 text-xs px-2 sm:px-3">
                  <Sparkle size={12} weight="fill" /> <span className="hidden sm:inline">Upgrade</span>
                </Button>
              </Link>
            )}
            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 rounded-full hover:bg-secondary/60 pl-1 pr-3 py-1 transition-colors" data-testid="user-menu-trigger">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={user.picture} alt={user.name} />
                      <AvatarFallback>{user.name?.[0]?.toUpperCase() || "U"}</AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:inline text-sm">{user.name}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="font-medium">{user.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/settings" data-testid="menu-settings"><Gear size={14} className="mr-2" /> Settings</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                    <SignOut size={14} className="mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        <nav className="md:hidden border-t border-border/60 bg-background">
          <div className="max-w-[1600px] mx-auto px-2 py-2 flex items-center gap-1 overflow-x-auto">
            {NAV.map((n) => {
              const active = location.pathname.startsWith(n.to);
              const Icon = n.icon;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`${n.testId}-mobile`}
                  className={`px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5 whitespace-nowrap ${
                    active ? "bg-secondary text-foreground" : "text-muted-foreground"
                  }`}
                >
                  <Icon size={14} weight={active ? "fill" : "regular"} />
                  {n.label}
                </Link>
              );
            })}
          </div>
        </nav>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 py-4 sm:py-8">{children}</main>
    </div>
  );
}
